import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  sis: "SIS",
  massnahmen: "Maßnahmenplan",
  pflegebericht: "Pflegebericht",
  kassenbrief: "Kassenbrief",
  hoehergruppe: "Höherstufung",
  qualitaet: "Qualitätscheck",
};

const STATUS_BADGE = {
  entwurf: "bg-cream-200 text-sand-700",
  pruefung: "bg-peach-100 text-terra border border-terra/30",
  freigegeben: "bg-sage-light text-sage-dark border border-sage-dark/30",
};

const STATUS_LABEL = {
  entwurf: "Entwurf",
  pruefung: "In Prüfung",
  freigegeben: "Freigegeben",
};

function fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}


/**
 * Liste aller Entwürfe des eingeloggten Users. Filter nach Kind + Status.
 */
export default function PkiDraftsList({ onLoad }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterKind) params.kind = filterKind;
      if (filterStatus) params.status = filterStatus;
      const r = await axios.get(`${API}/pflege-ki/drafts`, {
        params, withCredentials: true,
      });
      setDrafts(r.data?.drafts || []);
    } catch (e) {
      toast.error("Konnte Entwürfe nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterKind, filterStatus]);

  const del = async (id) => {
    if (!window.confirm("Entwurf wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/pflege-ki/drafts/${id}`, { withCredentials: true });
      toast.success("Entwurf gelöscht.");
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const downloadPdf = async (id, kind) => {
    try {
      const r = await axios.get(`${API}/pflege-ki/drafts/${id}/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pflege-KI-${kind}-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF-Export fehlgeschlagen.");
    }
  };

  return (
    <div className="space-y-4" data-testid="pki-drafts">
      <div className="rounded-2xl border border-cream-300 bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">
          Filter
        </div>
        <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)}
          data-testid="pki-drafts-filter-kind"
          className="h-9 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13px] focus:outline-none focus:border-terra">
          <option value="">Alle Typen</option>
          {Object.entries(KIND_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="pki-drafts-filter-status"
          className="h-9 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13px] focus:outline-none focus:border-terra">
          <option value="">Alle Status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <span className="ml-auto text-[12px] text-sand-500">
          {loading ? "…" : `${drafts.length} Einträge`}
        </span>
      </div>

      {drafts.length === 0 && !loading ? (
        <div className="rounded-2xl border border-dashed border-cream-300 bg-white p-10 text-center text-sand-500 text-[14px]">
          Noch keine gespeicherten Entwürfe. Wechsle zu einem KI-Tab und speichere deinen ersten Entwurf.
        </div>
      ) : (
        <ul className="space-y-2">
          {drafts.map((d) => (
            <li key={d.id} data-testid={`pki-draft-${d.id}`}
              className="rounded-2xl border border-cream-300 bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center rounded-md bg-sand-800 text-cream-50 px-2 h-6 text-[10.5px] font-bold uppercase tracking-[0.14em]">
                    {KIND_LABEL[d.kind] || d.kind}
                  </span>
                  <span className={`inline-flex items-center rounded-full px-2.5 h-6 text-[10.5px] font-bold ${STATUS_BADGE[d.status] || ""}`}>
                    {STATUS_LABEL[d.status] || d.status}
                  </span>
                  <span className="text-[12px] text-sand-500">
                    {fmtDate(d.updated_at)}
                  </span>
                </div>
                <div className="mt-1 text-[13.5px] text-sand-800 font-medium truncate">
                  {d.customer_label || <em className="text-sand-400">ohne Bezugslabel</em>}
                </div>
                {d.status === "freigegeben" && d.freigegeben_durch && (
                  <div className="text-[11.5px] text-sage-dark mt-0.5">
                    ✓ Freigegeben durch {d.freigegeben_durch} · {fmtDate(d.freigegeben_at)}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 shrink-0">
                <Button size="sm" variant="outline" onClick={() => onLoad(d)}
                  data-testid={`pki-draft-open-${d.id}`}
                  className="h-9 rounded-full border-clay">Öffnen</Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(d.id, d.kind)}
                  data-testid={`pki-draft-pdf-${d.id}`}
                  className="h-9 rounded-full border-clay">PDF</Button>
                <Button size="sm" variant="outline" onClick={() => del(d.id)}
                  data-testid={`pki-draft-delete-${d.id}`}
                  className="h-9 rounded-full border-terra text-terra hover:bg-peach-50">
                  Löschen
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
